import { useEffect, useState } from 'preact/hooks';
import { type Session, describeError } from '../api/client';
import { metadata, tasksBy, type MetadataKind } from '../api/collections';
import { entryRow, displayDate, weekdayLabel } from '../api/adapter';
import type { WireEntry, WireTaskMetadata } from '../api/wire';

import { SectionHeading } from '../components/composed/SectionHeading';
import { EmptyState } from '../components/feedback/EmptyState.jsx';
import { EntryLine } from '../components/composed/EntryLine';
import { Pressable } from '../components/a11y/Pressable';
import { SegmentedNav } from '../components/a11y/SegmentedNav';

const SIGIL: Record<MetadataKind, string> = { streams: '/', projects: '#', contexts: '@' };

/** The token colour per kind. A mnemonic for which sigil you are looking at —
 *  never the carrier of meaning, which is the sigil itself. */
const TOKEN_COLOUR: Record<MetadataKind, string> = {
  streams: 'var(--bujo-token-stream)',
  projects: 'var(--bujo-token-project)',
  contexts: 'var(--bujo-token-context)',
};

const KINDS: MetadataKind[] = ['streams', 'projects', 'contexts'];

/** The metadata kind as an entry token's kind, for suppressing the token a row
 *  was filtered by. */
const TOKEN_KIND: Record<MetadataKind, 'stream' | 'project' | 'context'> = {
  streams: 'stream',
  projects: 'project',
  contexts: 'context',
};

type StateFilter = 'open' | 'done' | 'migrated' | 'cancelled';

interface Chosen {
  kind: MetadataKind;
  name: string;
}

/** Streams, projects and contexts, and the tasks under one of them.
 *
 *  **All three on one page, not three tabbed lists.** The frame titles this
 *  screen `STREAMS · PROJECTS · CONTEXTS` and stacks the three as sections with
 *  an `ARCHIVED` group beneath — it is the journal's index of its own
 *  vocabulary, read at a glance. A segmented control hid two thirds of that
 *  behind a tap and retitled the page after whichever third was showing.
 *
 *  Three details in the frame have nothing behind them in the schema and are
 *  deliberately not faked, the same call as the Reading Log's invented
 *  `ABANDONED` status:
 *
 *   - `34 total` beside the open count. Only `active_task_count` exists; there
 *     is no total, and the entries page carries no count either. Streams show
 *     their real `project_count` instead, which keeps the frame's two-part
 *     rhythm with a fact.
 *   - `due 2026-09` on a project. There is no due date on `task_projects`. The
 *     parent stream is real and shown in its place.
 *   - `closed 30 JUN` in the archived group. `archived` is a boolean; no date is
 *     recorded.
 */
export function Filters({
  session,
  initial = null,
  onOpenActions,
  onCaptureInto,
}: {
  session: Session;
  /** Open straight onto one filter's tasks. The rail lists busy filters with
   *  their open counts, and a row labelled `/Product 12` that landed on the
   *  index of every stream would be a half-kept promise. */
  initial?: Chosen | null;
  onOpenActions: (entry: WireEntry) => void;
  /** The token the capture bar should capture into while a filter is open —
   *  `/Product` — or null at the index. The frame labels the bar
   *  `capture into /Product`, and a label without the behaviour would be a lie. */
  onCaptureInto?: (token: string | null) => void;
}) {
  const [lists, setLists] = useState<Record<MetadataKind, WireTaskMetadata[]> | null>(null);
  const [chosen, setChosen] = useState<Chosen | null>(initial);
  const [tasks, setTasks] = useState<{ items: WireEntry[]; truncated: boolean } | null>(null);
  const [filter, setFilter] = useState<StateFilter>('open');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [streams, projects, contexts] = await Promise.all(KINDS.map((k) => metadata(session, k)));
        if (!cancelled) {
          setLists({ streams: streams ?? [], projects: projects ?? [], contexts: contexts ?? [] });
        }
      } catch (caught) {
        if (!cancelled) setError(describeError(caught));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session]);

  useEffect(() => {
    onCaptureInto?.(chosen ? `${SIGIL[chosen.kind]}${chosen.name}` : null);
  }, [chosen, onCaptureInto]);

  useEffect(() => {
    if (!chosen) return;
    let cancelled = false;
    setTasks(null);
    setFilter('open');
    (async () => {
      try {
        const found = await tasksBy(session, chosen.kind, chosen.name);
        if (!cancelled) setTasks(found);
      } catch (caught) {
        if (!cancelled) setError(describeError(caught));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session, chosen]);

  if (chosen) {
    return (
      <FilteredTasks
        chosen={chosen}
        tasks={tasks}
        filter={filter}
        onFilter={setFilter}
        onBack={() => setChosen(null)}
        onOpenActions={onOpenActions}
        error={error}
      />
    );
  }

  return (
    <main style={page}>
      <header style={header}>
        <div style={eyebrow}>BUJO / INDEX</div>
        <h1 style={h1}>STREAMS · PROJECTS · CONTEXTS</h1>
      </header>

      {error ? <p style={errorLine}>! {error}</p> : null}

      {lists === null ? null : (
        <>
          {KINDS.map((kind) => {
            const live = lists[kind].filter((item) => !isArchived(item));
            return (
              <section key={kind} style={section}>
                <SectionHeading>{kind.toUpperCase()}</SectionHeading>
                {live.length === 0 ? (
                  <EmptyState
                    line={`No ${kind} yet.`}
                    detail={`Tag a task with ${SIGIL[kind]}name when you capture it.`}
                  />
                ) : (
                  live.map((item) => (
                    <MetadataRow
                      key={item.id}
                      kind={kind}
                      item={item}
                      onPress={() => setChosen({ kind, name: item.name })}
                    />
                  ))
                )}
              </section>
            );
          })}

          {/* Archived is shown, not hidden: a stream with history is still a
              fact about the journal. Dimmed and not tappable to filter, because
              its tasks live under whatever is current. */}
          {archivedAcross(lists).length > 0 ? (
            <section style={section}>
              <SectionHeading>ARCHIVED</SectionHeading>
              {archivedAcross(lists).map(({ kind, item }) => (
                <div key={`${kind}:${item.id}`} style={row}>
                  <span style={{ ...name, color: 'var(--bujo-text-meta)' }}>
                    {SIGIL[kind]}
                    {item.name}
                  </span>
                  <span style={meta}>{archivedNote(item)}</span>
                </div>
              ))}
            </section>
          ) : null}

          <p style={footnote}>
            Token colour is a mnemonic, not the state: the sigil{' '}
            <span style={{ color: TOKEN_COLOUR.streams }}>/</span>{' '}
            <span style={{ color: TOKEN_COLOUR.projects }}>#</span>{' '}
            <span style={{ color: TOKEN_COLOUR.contexts }}>@</span> carries the meaning. Counts are
            facts, never progress bars.
          </p>
        </>
      )}
    </main>
  );
}

function MetadataRow({
  kind,
  item,
  onPress,
}: {
  kind: MetadataKind;
  item: WireTaskMetadata;
  onPress: () => void;
}) {
  const open = item.active_task_count;
  return (
    <Pressable
      label={`${SIGIL[kind]}${item.name}, ${open} open ${open === 1 ? 'task' : 'tasks'}`}
      onPress={onPress}
    >
      <div style={row}>
        <span style={{ ...name, color: TOKEN_COLOUR[kind] }}>
          {SIGIL[kind]}
          {item.name}
        </span>
        <span style={meta}>{describeCounts(item)}</span>
      </div>
    </Pressable>
  );
}

/** The filtered task list. `Same entry row, same actions, same gestures as the
 *  Daily Log — a filter is a lens on the rapid log, not a second task system.`
 *  That line is from the frame and it is the whole design of this screen. */
function FilteredTasks({
  chosen,
  tasks,
  filter,
  onFilter,
  onBack,
  onOpenActions,
  error,
}: {
  chosen: Chosen;
  tasks: { items: WireEntry[]; truncated: boolean } | null;
  filter: StateFilter;
  onFilter: (next: StateFilter) => void;
  onBack: () => void;
  onOpenActions: (entry: WireEntry) => void;
  error: string | null;
}) {
  const all = tasks?.items ?? [];
  const counts: Record<StateFilter, number> = {
    open: all.filter((t) => t.state === 'open').length,
    done: all.filter((t) => t.state === 'done').length,
    migrated: all.filter((t) => t.state === 'migrated').length,
    cancelled: all.filter((t) => t.state === 'cancelled').length,
  };
  const shown = all.filter((t) => t.state === filter);
  const groups = byDay(shown);

  return (
    <main style={page}>
      <header style={header}>
        <div style={eyebrow}>BUJO / INDEX / {chosen.kind.toUpperCase()}</div>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 'var(--bujo-space-3)' }}>
          <h1 style={{ ...h1, color: TOKEN_COLOUR[chosen.kind], minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {SIGIL[chosen.kind]}
            {chosen.name}
          </h1>
          <span style={{ ...meta, flex: 'none' }}>{counts.open} OPEN</span>
        </div>
      </header>

      <button type="button" onClick={onBack} style={back}>
        ◀ all {chosen.kind}
      </button>

      {error ? <p style={errorLine}>! {error}</p> : null}

      <div style={{ paddingTop: 'var(--bujo-space-3)' }}>
        <SegmentedNav
          label="Task state"
          variant="boxed"
          active={filter}
          onSelect={onFilter}
          options={[
            { value: 'open', label: 'OPEN', count: counts.open },
            { value: 'done', label: 'DONE', count: counts.done },
            { value: 'migrated', label: 'MIGRATED', count: counts.migrated },
            { value: 'cancelled', label: 'CANCELLED', count: counts.cancelled },
          ]}
        />
      </div>

      <section style={{ paddingTop: 'var(--bujo-space-4)' }}>
        <SectionHeading>{filter.toUpperCase()} · BY DAY</SectionHeading>
        {tasks === null ? null : shown.length === 0 ? (
          <EmptyState line={`Nothing ${filter} under ${SIGIL[chosen.kind]}${chosen.name}.`} />
        ) : (
          groups.map((group) => (
            <div key={group.key}>
              <div style={dayHeader}>{group.label}</div>
              {group.rows.map((task) => (
                <EntryLine
                  key={task.id}
                  // The token you filtered by is dropped from every row: you are
                  // already looking at /Product, so repeating it on each line is
                  // ink with no information.
                  entry={entryRow(task, { kind: TOKEN_KIND[chosen.kind], name: chosen.name })}
                  onPress={() => onOpenActions(task)}
                />
              ))}
            </div>
          ))
        )}
        {tasks?.truncated ? (
          <p style={footnote}>
            Showing the first 100 tasks under this {singular(chosen.kind)} — the page limit. The
            counts above are of those 100, not of the whole journal.
          </p>
        ) : null}
      </section>
    </main>
  );
}

/** Group by the day a task sits on, dated first in date order, then the month
 *  inbox. `Day grouping is inclusive and never rolls up into a fake "this
 *  week"` — the frame's words, and the project's standing rule about rolling
 *  seven-day figures. */
function byDay(tasks: WireEntry[]): { key: string; label: string; rows: WireEntry[] }[] {
  const dated = new Map<string, WireEntry[]>();
  const months = new Map<string, WireEntry[]>();
  const loose: WireEntry[] = [];

  for (const task of tasks) {
    if (task.active_date) {
      const list = dated.get(task.active_date) ?? [];
      list.push(task);
      dated.set(task.active_date, list);
    } else if (task.active_month) {
      const list = months.get(task.active_month) ?? [];
      list.push(task);
      months.set(task.active_month, list);
    } else {
      loose.push(task);
    }
  }

  const groups = [...dated.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, rows]) => ({ key: date, label: dayHeading(date), rows }));

  for (const [month, rows] of [...months.entries()].sort(([a], [b]) => a.localeCompare(b))) {
    groups.push({
      key: `m${month}`,
      label: `NO DATE · MONTH INBOX ${month}`,
      rows,
    });
  }
  if (loose.length) groups.push({ key: 'loose', label: 'NO DATE', rows: loose });
  return groups;
}

/** `SUN 02 AUG`. */
function dayHeading(date: string): string {
  return `${weekdayLabel(date)} ${displayDate(date).slice(0, 6)}`;
}

/** Streams and contexts carry an `archived` boolean; projects carry a `status`
 *  of `active | completed | archived` and no boolean at all. Checking only the
 *  boolean silently left every finished project in the live list.
 *
 *  These narrow with `in` rather than reaching for an optional field, because
 *  the three are genuinely three schemas — the generated types made that
 *  explicit and it is worth keeping visible. */
const isArchived = (item: WireTaskMetadata) =>
  ('archived' in item && item.archived) ||
  ('status' in item && (item.status === 'archived' || item.status === 'completed'));

/** What the archived group says about a row. The frame shows `closed 30 JUN`;
 *  no closed date is recorded anywhere, so this reports the real status. */
const archivedNote = (item: WireTaskMetadata) =>
  'status' in item && item.status === 'completed' ? 'completed' : 'archived';

function archivedAcross(
  lists: Record<MetadataKind, WireTaskMetadata[]>,
): { kind: MetadataKind; item: WireTaskMetadata }[] {
  return KINDS.flatMap((kind) =>
    lists[kind].filter(isArchived).map((item) => ({ kind, item })),
  );
}

/** Real counts only. See the note on the component about what the frame asks
 *  for that the schema cannot supply. */
function describeCounts(item: WireTaskMetadata): string {
  const open = item.active_task_count === 0 ? 'none open' : `${item.active_task_count} open`;
  // A stream is the only one with a project count; a project is the only one
  // with a parent stream. `kind` used to be passed in and checked alongside an
  // optional field, which is two ways of asking the same question.
  if ('project_count' in item && item.project_count > 0) {
    return `${open} · ${item.project_count} project${item.project_count === 1 ? '' : 's'}`;
  }
  if ('stream' in item && item.stream) return `${open} · /${item.stream}`;
  return open;
}

const singular = (kind: MetadataKind) => kind.replace(/s$/, '');

const page = { padding: 'var(--bujo-gutter)', maxWidth: 560, margin: '0 auto' };
const header = {
  paddingBottom: 'var(--bujo-space-2)',
  marginBottom: 'var(--bujo-space-4)',
  borderBottom: '1px solid var(--bujo-rule-heavy)',
};
const eyebrow = {
  font: 'var(--bujo-fw-regular) var(--bujo-fs-label)/1 var(--bujo-font-mono)',
  letterSpacing: 'var(--bujo-ls-eyebrow)',
  color: 'var(--bujo-text-meta)',
  paddingBottom: 'var(--bujo-space-2)',
};
const h1 = {
  margin: 0,
  font: 'var(--bujo-fw-medium) var(--bujo-fs-display)/1.1 var(--bujo-font-mono)',
  color: 'var(--bujo-accent)',
};
const section = { paddingBottom: 'var(--bujo-space-5)' };
const row = {
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: 'var(--bujo-space-3)',
  minHeight: 'var(--bujo-row-min)',
  borderBottom: '1px solid var(--bujo-rule-hairline)',
};
const name = {
  font: 'var(--bujo-fw-regular) var(--bujo-fs-event)/var(--bujo-lh-row) var(--bujo-font-mono)',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
};
const meta = {
  flex: 'none',
  font: 'var(--bujo-fw-regular) var(--bujo-fs-meta)/1 var(--bujo-font-mono)',
  color: 'var(--bujo-text-meta)',
};
const dayHeader = {
  paddingTop: 'var(--bujo-space-4)',
  paddingBottom: 'var(--bujo-space-1)',
  font: 'var(--bujo-fw-regular) var(--bujo-fs-label)/1 var(--bujo-font-mono)',
  letterSpacing: 'var(--bujo-ls-label)',
  color: 'var(--bujo-text-meta)',
};
const footnote = {
  paddingTop: 'var(--bujo-space-4)',
  font: 'var(--bujo-fw-regular) var(--bujo-fs-micro)/1.6 var(--bujo-font-mono)',
  color: 'var(--bujo-text-meta)',
};
const back = {
  minHeight: 'var(--bujo-row-min)',
  font: 'var(--bujo-fw-regular) var(--bujo-fs-meta)/1 var(--bujo-font-mono)',
  color: 'var(--bujo-text-meta)',
  cursor: 'pointer',
};
const errorLine = {
  font: 'var(--bujo-fw-regular) var(--bujo-fs-meta)/1.4 var(--bujo-font-mono)',
  color: 'var(--bujo-state-error)',
};
